import type { InventoryLead } from '@otw/models/leads/inventoryLead'

export type InventoryLeadActiveFilters = {
  companyId?: string
  assignedRepId?: string
  year?: string
  month?: string
  truckType?: string
  truckMake?: string
  truckModel?: string
  truckSize?: string
  truckYear?: string
  lostReasonId?: string
}

export type InventoryLeadFilterOptions = {
  companies: Array<{ id: string; name: string }>
  truckTypes: string[]
  truckMakes: string[]
  truckModels: string[]
  truckSizes: string[]
  truckYears: string[]
  lostReasons: Array<{ id: string; label: string }>
}

const uniqSorted = (values: string[]): string[] =>
  [...new Set(values.filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }),
  )

/** Build filter dropdown options from the current inventory lead set. */
export function buildInventoryLeadFilterOptions(
  leads: InventoryLead[],
  resolveCompanyName: (companyId: string) => string,
): InventoryLeadFilterOptions {
  const companyMap = new Map<string, string>()
  const truckTypes: string[] = []
  const truckMakes: string[] = []
  const truckModels: string[] = []
  const truckSizes: string[] = []
  const truckYears: string[] = []
  const lostReasonMap = new Map<string, string>()

  for (const lead of leads) {
    if (lead.companyId) {
      const name = resolveCompanyName(lead.companyId)?.trim() || lead.companyId
      companyMap.set(lead.companyId, name)
    }
    if (lead.lostReasonId && lead.lostReasonLabel) {
      lostReasonMap.set(lead.lostReasonId, lead.lostReasonLabel)
    }
    for (const truck of lead.trucks ?? []) {
      if (truck.type?.trim()) truckTypes.push(truck.type.trim())
      if (truck.make?.trim()) truckMakes.push(truck.make.trim())
      if (truck.model?.trim()) truckModels.push(truck.model.trim())
      if (truck.size?.trim()) truckSizes.push(truck.size.trim())
      if (truck.year) truckYears.push(String(truck.year))
    }
  }

  return {
    companies: [...companyMap.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    truckTypes: uniqSorted(truckTypes),
    truckMakes: uniqSorted(truckMakes),
    truckModels: uniqSorted(truckModels),
    truckSizes: uniqSorted(truckSizes),
    truckYears: uniqSorted(truckYears),
    lostReasons: [...lostReasonMap.entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
  }
}

const leadMatchesTruckField = (
  lead: InventoryLead,
  value: string,
  field: 'type' | 'make' | 'model' | 'size' | 'year',
): boolean =>
  (lead.trucks ?? []).some((truck) => {
    if (field === 'year') return String(truck.year ?? '') === value
    const raw = truck[field]
    return typeof raw === 'string' && raw.trim() === value
  })

/** Apply inventory board filters (company, rep, date, truck attrs, lost reason). */
export function filterInventoryLeads(
  leads: InventoryLead[],
  filters: InventoryLeadActiveFilters,
): InventoryLead[] {
  let filtered = leads

  if (filters.companyId) {
    filtered = filtered.filter((lead) => lead.companyId === filters.companyId)
  }
  if (filters.assignedRepId) {
    filtered = filtered.filter(
      (lead) => String(lead.assignedRepId ?? '') === String(filters.assignedRepId),
    )
  }
  if (filters.year) {
    filtered = filtered.filter((lead) => {
      const created = lead.createdAt instanceof Date ? lead.createdAt : new Date(lead.createdAt)
      return created.getFullYear().toString() === filters.year
    })
    if (filters.month) {
      filtered = filtered.filter((lead) => {
        const created = lead.createdAt instanceof Date ? lead.createdAt : new Date(lead.createdAt)
        return created.getMonth().toString() === filters.month
      })
    }
  }
  if (filters.truckType) {
    filtered = filtered.filter((lead) => leadMatchesTruckField(lead, filters.truckType!, 'type'))
  }
  if (filters.truckMake) {
    filtered = filtered.filter((lead) => leadMatchesTruckField(lead, filters.truckMake!, 'make'))
  }
  if (filters.truckModel) {
    filtered = filtered.filter((lead) => leadMatchesTruckField(lead, filters.truckModel!, 'model'))
  }
  if (filters.truckSize) {
    filtered = filtered.filter((lead) => leadMatchesTruckField(lead, filters.truckSize!, 'size'))
  }
  if (filters.truckYear) {
    filtered = filtered.filter((lead) => leadMatchesTruckField(lead, filters.truckYear!, 'year'))
  }
  if (filters.lostReasonId) {
    filtered = filtered.filter((lead) => lead.lostReasonId === filters.lostReasonId)
  }

  return filtered
}

export const countInventoryActiveFilters = (filters: InventoryLeadActiveFilters): number =>
  Object.values(filters).filter((value) => value != null && String(value).trim() !== '').length
